// 主题管理 Composable
import { ref, computed, watch } from 'vue'

// 全局主题状态
const isDark = ref(false)
const theme = ref('light')

/**
 * 主题管理 Hook
 * @returns {Object} 主题相关的状态和方法
 */
export const useTheme = () => {
  // 初始化主题
  const initTheme = () => {
    try {
      // 从本地存储读取主题设置（兼容H5和uni-app环境）
      let savedTheme = ''
      try {
        savedTheme = typeof uni !== 'undefined' ? uni.getStorageSync('theme') : localStorage.getItem('theme')
      } catch (e) {
        savedTheme = localStorage.getItem('theme')
      }
      
      if (savedTheme) {
        theme.value = savedTheme
        isDark.value = savedTheme === 'dark'
      } else {
        // 根据系统主题自动设置
        if (typeof uni !== 'undefined' && uni.getSystemInfoSync) {
          try {
            const systemInfo = uni.getSystemInfoSync()
            if (systemInfo.theme) {
              isDark.value = systemInfo.theme === 'dark'
              theme.value = systemInfo.theme
            }
          } catch (e) {
            // 如果uni API失败，使用CSS媒体查询检测
            if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
              isDark.value = true
              theme.value = 'dark'
            }
          }
        } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
          // H5环境使用CSS媒体查询检测系统主题
          isDark.value = true
          theme.value = 'dark'
        }
      }
    } catch (error) {
      console.warn('初始化主题失败:', error)
      // 默认使用浅色主题
      isDark.value = false
      theme.value = 'light'
    }
    
    // 应用主题
    applyTheme()
  }

  // 切换主题
  const toggleTheme = () => {
    isDark.value = !isDark.value
    theme.value = isDark.value ? 'dark' : 'light'
    
    // 保存到本地存储（兼容H5和uni-app环境）
    saveThemeToStorage(theme.value)
    
    // 应用主题
    applyTheme()
  }

  // 设置指定主题
  const setTheme = (newTheme) => {
    if (newTheme !== 'light' && newTheme !== 'dark') {
      console.warn('无效的主题:', newTheme)
      return
    }
    
    theme.value = newTheme
    isDark.value = newTheme === 'dark'
    
    // 保存到本地存储（兼容H5和uni-app环境）
    saveThemeToStorage(theme.value)
    
    // 应用主题
    applyTheme()
  }

  // 保存主题到本地存储的辅助函数
  const saveThemeToStorage = (themeValue) => {
    try {
      if (typeof uni !== 'undefined' && uni.setStorageSync) {
        uni.setStorageSync('theme', themeValue)
      } else {
        localStorage.setItem('theme', themeValue)
      }
    } catch (error) {
      // 如果uni API失败，回退到localStorage
      try {
        localStorage.setItem('theme', themeValue)
      } catch (e) {
        console.warn('保存主题设置失败:', error)
      }
    }
  }

  // 应用主题到页面
  const applyTheme = () => {
    try {
      // H5环境中通过CSS变量和类名控制主题
      const root = document.documentElement
      
      if (isDark.value) {
        root.classList.add('dark-theme')
        root.classList.remove('light-theme')
        
        // 设置CSS变量
        root.style.setProperty('--primary-color', '#409eff')
        root.style.setProperty('--bg-color', '#141414')
        root.style.setProperty('--text-color', '#ffffff')
        root.style.setProperty('--border-color', '#333333')
        root.style.setProperty('--card-bg', '#1a1a1a')
      } else {
        root.classList.add('light-theme')
        root.classList.remove('dark-theme')
        
        // 设置CSS变量
        root.style.setProperty('--primary-color', '#2490ff')
        root.style.setProperty('--bg-color', '#f5f5f5')
        root.style.setProperty('--text-color', '#333333')
        root.style.setProperty('--border-color', '#eeeee')
        root.style.setProperty('--card-bg', '#ffffff')
      }

      // 更新页面主题色meta标签
      const themeColorMeta = document.querySelector('meta[name="theme-color"]')
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', isDark.value ? '#1a1a1a' : '#2490ff')
      }

      // 如果在uni-app环境中且API可用，则调用原生API
      if (typeof uni !== 'undefined' && uni.setNavigationBarColor) {
        uni.setNavigationBarColor({
          frontColor: isDark.value ? '#ffffff' : '#000000',
          backgroundColor: isDark.value ? '#1a1a1a' : '#ffffff',
          animation: {
            duration: 400,
            timingFunc: 'easeIn'
          }
        })
      }

      if (typeof uni !== 'undefined' && uni.setTabBarStyle) {
        uni.setTabBarStyle({
          color: isDark.value ? '#999999' : '#444444',
          selectedColor: isDark.value ? '#409eff' : '#2490ff',
          backgroundColor: isDark.value ? '#1a1a1a' : '#ffffff',
          borderStyle: isDark.value ? 'black' : 'white'
        })
      }
    } catch (error) {
      console.warn('应用主题样式失败:', error)
    }
  }

  // 计算属性
  const themeVars = computed(() => ({
    // 基础颜色
    primary: isDark.value ? '#409eff' : '#2490ff',
    secondary: isDark.value ? '#67c23a' : '#67c23a',
    success: isDark.value ? '#67c23a' : '#67c23a',
    warning: isDark.value ? '#e6a23c' : '#e6a23c',
    danger: isDark.value ? '#f56c6c' : '#f56c6c',
    info: isDark.value ? '#909399' : '#909399',

    // 背景颜色
    bgPrimary: isDark.value ? '#141414' : '#ffffff',
    bgSecondary: isDark.value ? '#1a1a1a' : '#f5f5f5',
    bgTertiary: isDark.value ? '#262626' : '#fafafa',

    // 文字颜色
    textPrimary: isDark.value ? '#ffffff' : '#303133',
    textRegular: isDark.value ? '#e4e7ed' : '#606266',
    textSecondary: isDark.value ? '#c0c4cc' : '#909399',
    textPlaceholder: isDark.value ? '#a8abb2' : '#c0c4cc',

    // 边框颜色
    borderBase: isDark.value ? '#4c4d4f' : '#dcdfe6',
    borderLight: isDark.value ? '#414243' : '#e4e7ed',
    borderLighter: isDark.value ? '#363637' : '#ebeef5',
    borderExtraLight: isDark.value ? '#2b2b2c' : '#f2f6fc',

    // 填充颜色
    fillBase: isDark.value ? '#303133' : '#f0f2f5',
    fillLight: isDark.value ? '#262727' : '#f5f7fa',
    fillLighter: isDark.value ? '#1d1e1f' : '#fafafa',
    fillExtraLight: isDark.value ? '#191a1b' : '#fefefe'
  }))

  // 监听系统主题变化
  const handleSystemThemeChange = () => {
    try {
      uni.onThemeChange && uni.onThemeChange((res) => {
        if (theme.value === 'auto') {
          isDark.value = res.theme === 'dark'
          applyTheme()
        }
      })
    } catch (error) {
      console.warn('监听系统主题变化失败:', error)
    }
  }

  // 获取当前主题的CSS类名
  const getThemeClass = (baseClass = '') => {
    const themeClass = isDark.value ? 'dark-theme' : 'light-theme'
    return baseClass ? `${baseClass} ${themeClass}` : themeClass
  }

  // 监听主题变化
  watch(
    () => isDark.value,
    (newVal) => {
      // 触发主题变化事件（兼容H5和uni-app环境）
      try {
        if (typeof uni !== 'undefined' && uni.$emit) {
          uni.$emit('themeChange', {
            isDark: newVal,
            theme: theme.value
          })
        } else if (typeof window !== 'undefined') {
          // H5环境使用自定义事件
          const event = new CustomEvent('themeChange', {
            detail: {
              isDark: newVal,
              theme: theme.value
            }
          })
          window.dispatchEvent(event)
        }
      } catch (error) {
        console.warn('主题变化事件触发失败:', error)
      }
    }
  )

  return {
    // 状态
    isDark,
    theme,
    themeVars,

    // 方法
    initTheme,
    loadTheme: initTheme, // 为了兼容性，loadTheme 指向 initTheme
    toggleTheme,
    setTheme,
    applyTheme,
    getThemeClass,
    handleSystemThemeChange
  }
}

// 自动初始化主题（在应用启动时调用）
let isInitialized = false
export const initGlobalTheme = () => {
  if (!isInitialized) {
    const { initTheme, handleSystemThemeChange } = useTheme()
    initTheme()
    handleSystemThemeChange()
    isInitialized = true
  }
}

export default useTheme
